<template>
  <div class="wallet-page">
    <div class="wallet-page__container">
      <!-- Header -->
      <div class="wallet-page__header">
        <div class="wallet-page__header-info">
          <div class="wallet-page__header-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h1 class="wallet-page__title">My Wallet</h1>
            <p class="wallet-page__subtitle">Manage your funds across multiple currencies</p>
          </div>
        </div>
      </div>

      <!-- Main Wallet Card -->
      <div class="wallet-card">
        <div class="wallet-card__balance">
          <div class="wallet-card__balance-head">
            <span class="wallet-card__balance-label">Available Balance</span>
          <div class="wallet-card__currency-selector">
            <div class="wallet-card__dropdown" @click="toggleCurrencyDropdown">
              <div class="wallet-card__dropdown-selected">
                <CurrencyFlag :currency="selectedCurrency" />
                <span class="wallet-card__dropdown-text">{{ selectedCurrency }}</span>
                <svg class="wallet-card__dropdown-icon" :class="{ 'wallet-card__dropdown-icon--open': showCurrencyDropdown }" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              
              <div class="wallet-card__dropdown-menu" v-if="showCurrencyDropdown">
                <button
                  v-for="wallet in walletBalance"
                  :key="wallet.currency"
                  class="wallet-card__dropdown-item"
                  :class="{ 'wallet-card__dropdown-item--active': wallet.currency === selectedCurrency }"
                  @click.stop="selectCurrency(wallet.currency)"
                >
                  <CurrencyFlag :currency="wallet.currency" />
                  <span>{{ wallet.currency }}</span>
                  <span class="wallet-card__dropdown-balance">{{ formatAmountToDollar(wallet.balance, 2, wallet.currency) }}</span>
                </button>
              </div>
            </div>
          </div>
          </div>

          <span class="wallet-card__balance-amount">{{ formatAmountToDollar(currentWalletBalance, 2, selectedCurrency) }}</span>
          <!-- Committing to a book moves treasury from balance into a hold on the same
               wallet, so the figure above drops with nothing to account for it. Only shown
               when something is actually held. -->
          <router-link v-if="heldForBids > 0" class="wallet-card__held" :to="{ name: 'MyInvestments' }">
            {{ formatAmountToDollar(heldForBids, 2, selectedCurrency) }} held for open bids
          </router-link>
        </div>

        <div class="wallet-card__actions">
          <button 
            class="wallet-card__btn wallet-card__btn--primary"
            @click="openFundModal"
            v-can.disable="'wallet:fundWallet'"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4.16667V15.8333M4.16667 10H15.8333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Fund
          </button>
          <button 
            class="wallet-card__btn wallet-card__btn--secondary"
            @click="openWithdrawModal"
            v-can.disable="'wallet:withdrawWallet'"
            :disabled="currentWalletBalance <= 0"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 15.8333V4.16667M10 4.16667L5 9.16667M10 4.16667L15 9.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Withdraw
          </button>
          <button 
            class="wallet-card__btn wallet-card__btn--outline"
            @click="openConvertModal"
            :disabled="currentWalletBalance <= 0 || walletBalance.length < 2"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.6667 7.5L13.3333 4.16667M13.3333 4.16667L10 7.5M13.3333 4.16667V12.5M3.33333 12.5L6.66667 15.8333M6.66667 15.8333L10 12.5M6.66667 15.8333V7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Convert
          </button>
        </div>
      </div>

      <!-- Transactions Table -->
      <div class="wallet-page__content">
        <TableHeader
          :options="options"
          :tab="option"
          label="options"
          @emitOption="emitOption"
        />
        <WalletTransactionTable
          v-if="option === 'Wallet Transactions'"
          :table="table"
        />
      </div>

      <!-- Withdraw Modal -->
      <WithdrawModal
        v-if="showWithdrawModal"
        title="Withdraw Cash"
        :subTitle="subTitle"
        :form="withdrawForm"
        :showBackButton="withdrawForm !== 'withdrawOptionsForm'"
        @close="closeModal"
        @goBack="goBack"
        @mounted="refresh"
      >
        <template v-slot:content>
          <AmountForm
            v-if="withdrawForm === 'amountForm'"
            class="tw-px-4 md:tw-px-10"
            :withdrawMethod="withdrawMethod"
            :availableCash="cashBalance"
            :minimumWithdrawal="minimumWithdrawal"
            :rate="Number(exchangeRate)"
            @close="showWithdrawModal = false"
            @storeData="switchWithdrawForm"
          />
          <WithdrawOptionsForm
            v-if="withdrawForm === 'withdrawOptionsForm'"
            class="tw-px-4"
            :bankDetails="bankDetails"
            :minimumWithdrawal="minimumWithdrawal"
            @storeData="switchWithdrawForm"
          />
          <SelectCountryForm
            v-if="withdrawForm === 'selectCountryForm'"
            class="tw-px-4"
            @bankConnected="switchWithdrawForm"
          />
          <WalletList
            v-if="withdrawForm === 'wallets'"
            @wallet-select="withdrawFromWallet"
          />
          <ConfirmationForm
            v-if="withdrawForm === 'confirmationForm'"
            class="tw-px-4"
            :fees="withdrawFees"
            :loading="btnLoading"
            @withdraw="withdrawRequest"
          />
        </template>
      </WithdrawModal>

      <!-- Fund Modal -->
      <FundWalletModal
        :visible="showFundModal"
        @close="showFundModal = false"
      />

      <!-- Convert Modal -->
      <ConvertModal
        v-if="showConvertModal"
        :fromCurrency="selectedCurrency"
        :availableBalance="currentWalletBalance"
        :wallets="otherWallets"
        @close="showConvertModal = false"
        @success="showConvertModal = false"
      />
    </div>
  </div>
</template>

<script>
import SelectCountryForm from "@/components/general/SelectCountryForm.vue";
import { default as WithdrawModal } from "@/components/general/sidemodal/SideModal.vue";
import EmptyState from "@/components/notification/EmptyState.vue";
import TableHeader from "@/components/tables/TableHeader";
import WalletTransactionTable from "@/components/tables/wallet/WalletTransactionTable.vue";
import CurrencyFlag from "@/components/wallet/CurrencyFlag.vue";
import SelectCurrencyForm from "@/components/wallet/fund/SelectCurrencyForm.vue";
import WalletList from "@/components/wallet/withdraw/WalletList";
import AmountForm from "@/components/wallet/withdraw/WithdrawAmountForm.vue";
import ConfirmationForm from "@/components/wallet/withdraw/WithdrawConfirmationForm.vue";
import WithdrawOptionsForm from "@/components/wallet/withdraw/WithdrawOptionsForm.vue";
import ConvertModal from "@/components/wallet/ConvertModal.vue";
import { useFundingProvider } from "@/composables/fundingProvider";
import { readFundingRedirect } from "@/composables/notifyUserFunding";
import addBankApi from "@/utils/addBankApi";
import api from "@/utils/api.js";
import { convertToLocale } from "@/utils/helpers";
import { formatAmountToDollar, isNumber } from "@/utils/helpers.js";
import responseHandler from "@/utils/responseHandler.js";
import { getItem, removeItem } from "@/utils/storage.js";
import { computed } from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import FundWalletModal from "@/components/wallet/FundModal";

const { providerFunding } = useFundingProvider();

export default {
  name: "Wallet",

  components: {
    WithdrawModal,
    CurrencyFlag,
    TableHeader,
    WalletList,
    WalletTransactionTable,
    EmptyState,
    AmountForm,
    WithdrawOptionsForm,
    ConfirmationForm,
    SelectCountryForm,
    SelectCurrencyForm,
    FundWalletModal,
    ConvertModal,
  },

  data() {
    return {
      loading: false,
      showWithdrawModal: false,
      showFundModal: false,
      showConvertModal: false,
      showCurrencyDropdown: false,
      selectedCurrency: "USD",
      btnLoading: false,
      isAllowed: false,
      // Money only. Holdings and bids moved to Treasury > My Investments: the wallet is
      // cash, that page is what the cash became.
      options: ["Wallet Transactions"],
      option: "",
      currentOption: "",
      subTitle: "",
      withdrawForm: "",
      withdrawFees: 0,
      swapCurrency: "",
      fundPayload: {},
    };
  },

  computed: {
    ...mapState({
      tradeBalance: (state) => state.organisationModule.tradeBalance,
      minimumWithdrawal: (state) => state.organisationModule.minimumWithdrawal,
      entityCard: (state) => state.organisationModule.entityCard,
      bankDetails: (state) => state.Account.bankAccounts,
      fundMethod: (state) => state.organisationModule.fundMethod,
      withdrawMethod: (state) => state.organisationModule.withdrawMethod,
      currency: (state) => state.organisationModule.currency,
      exchangeRate: (state) => state.organisationModule.exchangeRate,
    }),
    ...mapGetters("organisationModule", ["walletBalance", "virtualAccount", "currencyWallets"]),

    // The buckets come from /token/currency-wallets, not the balance endpoint — that one
    // returns a bare number per gToken and has no room for them.
    heldForBids() {
      const bucket = (this.currencyWallets || []).find(
        (w) => w.currency === this.selectedCurrency
      );
      return Number(bucket?.offer_hold || 0);
    },

    getProvider() {
      let provider = null;
      switch (this.selectedCurrency) {
        case "USD":
          provider = "flutterwave";
          break;
        case "GBP":
          provider = "flutterwave";
          break;
        case "EUR":
          provider = "flutterwave";
          break;
        case "NGN":
        default:
          provider = "transactpay";
          break;
      }
      return provider;
    },
    currentWalletBalance() {
      if (!Array.isArray(this.walletBalance)) return 0;
      const wallet = this.walletBalance.find(
        (w) => w.currency === this.selectedCurrency
      );
      return wallet?.balance || 0;
    },
    cashBalance() {
      if (!Array.isArray(this.walletBalance)) return 0;
      return this.walletBalance.find(
        (wallet) => wallet.currency === this.selectedCurrency
      )?.balance || 0;
    },
    otherWallets() {
      if (!Array.isArray(this.walletBalance)) return [];
      return this.walletBalance.filter(
        (w) => w.currency !== this.selectedCurrency
      );
    },
  },
  provide() {
    return {
      provider: computed(() => this.getProvider),
      swapCurrency: computed(() => this.swapCurrency),
    };
  },

  watch: {
    walletBalance: {
      handler(newVal) {
        // Set initial currency when wallet data loads
        if (Array.isArray(newVal) && newVal.length > 0 && this.selectedCurrency === "USD") {
          this.selectedCurrency = newVal[0].currency;
        }
      },
      immediate: true,
    },
  },

  created() {
    this.getWithdrawalDetails();
    // The buckets carry offer_hold, which explains why the spendable balance is lower
    // than expected while bids are live.
    this.getCurrencyWallets();
  },

  mounted() {
    removeItem("fundPayload");
    // Set initial currency from first wallet
    if (Array.isArray(this.walletBalance) && this.walletBalance.length > 0) {
      this.selectedCurrency = this.walletBalance[0].currency;
    }
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.closeCurrencyDropdown);
    
    const query = this.$route.query;
    if (query.ref) {
      const data = {
        code: query.ref,
      };
      this.finalizeBankConnection("nordigen", data);
      return;
    }
    if (query.code && query.state) {
      let redirectUri = `${window.location.origin}/dashboard/wallet`;
      const data = {
        verifier: query.state,
        code: query.code,
        redirectUri,
      };
      this.finalizeBankConnection("stitch", data);
      return;
    }
    const fundingRedirect = readFundingRedirect(query);
    if (fundingRedirect.reference && fundingRedirect.status === "successful") {
      this.getWalletBalance();
      this.showToast({
        description: "Wallet funding successful.",
        display: true,
        type: "success",
      });
      this.$router.push({ name: "Wallet" });
      return;
    }
  },

  methods: {
    ...mapActions("organisationModule", [
      "getWalletBalance",
      "getCurrencyWallets",
      "generateVirtualAccount",
      "getEntityCard",
    ]),




    ...mapActions("Account", ["fetchBankAccounts"]),
    ...mapActions(["showToast", "setLoading"]),

    formatAmountToDollar,
    isNumber,
    providerFunding,

    // Currency dropdown methods
    toggleCurrencyDropdown() {
      this.showCurrencyDropdown = !this.showCurrencyDropdown;
    },
    closeCurrencyDropdown(e) {
      if (!e.target.closest('.wallet-card__dropdown')) {
        this.showCurrencyDropdown = false;
      }
    },
    selectCurrency(currency) {
      this.selectedCurrency = currency;
      this.$store.dispatch("organisationModule/setCurrency", currency);
      this.showCurrencyDropdown = false;
    },

    // Modal methods
    openFundModal() {
      this.$store.dispatch("organisationModule/setCurrency", this.selectedCurrency);
      this.showFundModal = true;
    },
    openWithdrawModal() {
      this.$store.dispatch("organisationModule/setCurrency", this.selectedCurrency);
      this.showWithdrawModal = true;
    },
    openConvertModal() {
      this.showConvertModal = true;
    },

    async getWithdrawalDetails(page) {
      this.setLoading(true);
      let bankDetails = this.fetchBankAccounts();
      let walletBalance = this.getWalletBalance();
      let entityCards = this.getEntityCard();
      try {
        await Promise.all([bankDetails, walletBalance, entityCards]);
        this.setLoading(false);
      } catch (error) {
        responseHandler.handleError(error);
        this.setLoading(false);
      }
    },

    async withdrawRequest(payload) {
      this.btnLoading = true;
      try {
        if (payload.bank_id) {
          const response = await api.createWithdrawalRequest(payload);
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
        } else {
          const amount = { amount: payload.tokens };
          const response = await api.withdrawToTradeWallet(amount);
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
        }
        await this.getWithdrawalDetails();
        this.btnLoading = false;
        this.showWithdrawModal = false;
        removeItem("withdrawalPayload");
      } catch (error) {
        responseHandler.handleError(error);
        this.btnLoading = false;
      }
    },
    async switchWithdrawForm(form, currency) {
      const fundDetails = getItem("fundPayload");
      switch (form) {
        case "amountForm":
          await this.calculateWithdrawalFees();
          this.withdrawForm = "confirmationForm";
          this.subTitle = "Confirmation";
          break;
        case "withdrawOptionsForm":
          this.withdrawForm = "amountForm";
          this.subTitle = "How much do you want to withdraw?";
          break;
        case "selectCountryForm":
          // A bank added here is withdrawn from its own currency's wallet.
          if (currency) this.$store.dispatch("organisationModule/setCurrency", currency);
          this.withdrawForm = "withdrawOptionsForm";
          this.subTitle = "";
          break;
        case "Add Bank":
          this.withdrawForm = "selectCountryForm";
          this.subTitle = "";
          break;
        case "Other GetEquity Wallet":
          this.withdrawForm = "wallets";
          this.subTitle = "Which wallet are you withdrawing to?";
          break;
        case "walletList":
          this.withdrawForm = "amountForm";
          this.subTitle = "How much do you want to withdraw?";
          break;
        case "Fund with bank transfer":
          this.fundForm = "fundAmountForm";
          this.fundSubTitle = "How much do you want to fund?";
          this.fundOptions = "Fund with bank transfer";
          break;
        case "Fund with debit card":
          this.fundForm = "fundAmountForm";
          this.fundSubTitle = "How much do you want to fund?";
          this.fundOptions = "Fund with debit card";
          break;
        case "CardOptions":
          this.fundForm = "fundConfirmationForm";
          this.fundSubTitle = "Payment Confirmation";
          break;
        case "storedCard":
          this.fundForm = "fundConfirmationForm";
          this.fundSubTitle = "Payment Confirmation";
          this.fundOptions = "Fund with stored card";
          break;
        case "newCard":
          this.fundForm = "fundConfirmationForm";
          this.fundSubTitle = "Payment Confirmation";
          this.fundOptions = "Fund with Cash";
          break;
        case "fundAmountForm":
          if (this.entityCard.length === 0) {
            this.fundForm = "fundConfirmationForm";
            this.fundSubTitle = "Payment Confirmation";
          } else {
            this.fundForm = "CardOptions";
            this.fundSubTitle = "Select Debit Card";
          }
          break;
        case "fundConfirmationForm":
          const payload = {
            currency: this.currency,
            provider: this.getProvider,
          };
          if (this.fundOptions === "Fund with bank transfer") {
            const response = await this.generateVirtualAccount(payload);
            if (response) {
              this.fundForm = "DedicatedVirtualAccountForm";
              this.fundSubTitle = `<p class="tw-text-gray-lightest3">Transfer a total amount of <span class='tw-text-primary tw-font-bold'>${
                this.currency
              } ${convertToLocale(
                fundDetails.totalAmount
              )}</span> to this account to fund your wallet.</p>`;
            }
            return;
          }
          if (
            this.virtualAccount.account_number &&
            this.fundMethod === "virtual"
          ) {
            this.fundForm = "DedicatedVirtualAccountForm";
            this.fundSubTitle = `<p class="tw-text-gray-lightest3">Transfer a total amount of <span class='tw-text-primary tw-font-bold'>${
              this.currency
            } ${convertToLocale(
              fundDetails.totalAmount
            )}</span> to this account to fund your wallet.</p>`;
          }
          if (this.fundMethod === "fiat") {
            this.fundForm = "SelectDebitCardForm";
            this.fundSubTitle = `Select card to fund <span class='tw-text-primary tw-font-bold'>${
              this.currency
            } ${convertToLocale(fundDetails.totalAmount)}</span> with`;
          }
          break;
        case "Another wallet":
          this.fundForm = "walletList";
          this.fundSubTitle = "Where would you like to convert from?";
          break;
        case "fundWalletList":
          this.fundForm = "fundAmountForm";
          this.fundSubTitle = "How much do you want to convert?";
          this.fundOptions = `${this.swapCurrency} Wallet`;
          break;
      }
    },
    withdrawFromWallet(currency) {
      this.swapCurrency = currency;
      this.switchWithdrawForm("walletList");
    },
    fundFromWallet(currency) {
      this.swapCurrency = currency;
      this.switchWithdrawForm("fundWalletList");
    },

    async finalizeBankConnection(type, data) {
      this.setLoading(true);
      this.showWithdrawModal = true;
      setTimeout(() => {
        this.$router.push({ name: "Wallet" });
        this.switchWithdrawForm("amountForm");
      }, 100);
      try {
        const response = await addBankApi.finalizeBankConnection(type, data);
        await this.fetchBankAccounts();
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        this.$router.push({ name: "Wallet" });
        this.setLoading(false);
      } catch (error) {
        responseHandler.handleError(error);
        this.setLoading(false);
      }
    },

    emitOption(option) {
      switch (option) {
        case "Wallet Transactions":
          this.table = "walletTransactions";
          this.option = "Wallet Transactions";
          break;

        default:
          break;
      }
    },
    async calculateWithdrawalFees() {
      const currency = getItem("withdrawalPayload")?.currency;
      const amount = getItem("withdrawalPayload")?.amount;
      let currencyObject;
      let percentFee;
      let minFee;
      const response = await api.withdrawFees();
      const { data } = response.data;
      const keys = Object.keys(data);
      keys.forEach((key, index) => {
        if (key === currency) {
          currencyObject = data[key];
          const percent = currencyObject.percent ?? currencyObject.percentage;
          switch (!!percent) {
            case true:
              if (this.withdrawMethod === "Trading Wallet") {
                this.withdrawFees = 0;
                return;
              }
              percentFee = Number((percent / 100) * amount);
              minFee = Number(currencyObject.fee);
              minFee > percentFee
                ? (this.withdrawFees = minFee)
                : (this.withdrawFees = percentFee);
              break;
            case false:
              this.withdrawFees = Number(currencyObject.fee);
              break;

            default:
              break;
          }
        }
      });
    },

    closeModal(modal) {
      switch (modal) {
        case "Withdraw Cash":
          this.showWithdrawModal = !this.showWithdrawModal;
          removeItem("withdrawalPayload");
          break;
        case "Fund Wallet":
          this.showFundModal = !this.showFundModal;
          removeItem("fundPayload");
          break;

        default:
          break;
      }
    },

    goBack(form) {
      const fundDetails = getItem("fundPayload");
      switch (form) {
        case "amountForm":
          this.withdrawForm = "withdrawOptionsForm";
          this.subTitle = "";
          break;
        case "confirmationForm":
          this.withdrawForm = "amountForm";
          this.subTitle = "How much do you want to withdraw?";
          break;
        case "wallets":
        case "selectCountryForm":
          this.withdrawForm = "withdrawOptionsForm";
          this.subTitle = "";
          break;
        case "SelectCurrencyForm":
          this.fundForm = "fundOptionsForm";
          this.fundSubTitle = "How do you want to fund your wallet";
          break;
        case "fundProvider":
          providerFunding(this.getProvider);
          break;
        case "fundAmountForm":
          this.fundForm = "fundOptionsForm";
          this.fundSubTitle = "How do you want to fund your wallet?";
          break;
        // case "fundOptionsForm":
        // 	this.fundForm = "fundAmountForm";
        // 	this.fundSubTitle = "How much do you want to fund?";
        // 	break;
        case "fundConfirmationForm":
          this.fundForm = "fundAmountForm";
          this.fundSubTitle = "How much do you want to fund?";
          break;
        case "DedicatedVirtualAccountForm":
          this.fundForm = "fundConfirmationForm";
          fundSubTitle = `<p class="tw-text-gray-lightest3">Transfer a total amount of <span class='tw-text-primary tw-font-bold'>${
            this.currency
          } ${convertToLocale(
            fundDetails.totalAmount
          )}</span> to this account to fund your wallet.</p>`;
          break;
        case "CardOptions":
          this.fundForm = "fundAmountForm";
          this.fundSubTitle = "How much do you want to fund?";
          break;

        default:
          break;
      }
    },

    refresh(modal) {
      switch (modal) {
        case "Fund Wallet":
          this.fundForm = "fundOptionsForm";
          this.fundSubTitle = "How do you want to fund your wallet";
          break;
        case "Withdraw Cash":
          this.withdrawForm = "withdrawOptionsForm";
          this.subTitle = "";
          break;

        default:
          break;
      }
    },

    gotoPage(page) {
      this.getWithdrawalDetails(page);
    },
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closeCurrencyDropdown);
  },
};
</script>

<style lang="scss" scoped>
.wallet-page {
  width: 100%;
  min-height: 100vh;
  background: #F8FAFC;
  padding: 24px;

  @media screen and (min-width: 768px) {
    padding: 32px;
  }

  &__container {
    max-width: 1280px;
    margin: 0 auto;
  }

  &__header {
    margin-bottom: 32px;
  }

  &__header-info {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__header-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #4D84FF;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 24px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-top: 24px;
    border: 1px solid #E2E8F0;
  // Flatter than a drop shadow, which at this size reads dated. A hairline border plus a
  // very soft lift keeps the edge crisp against the #F8FAFC page.
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
}

.wallet-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #E2E8F0;
  // Flatter than a drop shadow, which at this size reads dated. A hairline border plus a
  // very soft lift keeps the edge crisp against the #F8FAFC page.
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  @media screen and (min-width: 768px) {
    padding: 32px;
  }

  &__header {
    margin-bottom: 24px;
  }

  &__label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }

  &__currency-selector {
    /* Keeps the menu's z-index local instead of competing with the page. */
    position: relative;
    isolation: isolate;
    max-width: 220px;
    flex-shrink: 0;
  }

  &__dropdown {
    /* Sits on the navy panel now, so it reads as a control on a dark surface rather than
       a white input floating above it. */
    border-color: rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;

    &:hover {
      background: rgba(255, 255, 255, 0.14);
    }

    position: relative;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 12px 16px;
    cursor: pointer;
    transition: border-color 150ms ease, background-color 150ms ease;

    &:hover {
      border-color: #4D84FF;
    }
  }

  &__dropdown-selected {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  &__dropdown-text {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #ffffff;
    flex: 1;
  }

  &__dropdown-icon {
    color: rgba(255, 255, 255, 0.7);
    transition: transform 0.2s ease;
    flex-shrink: 0;
    margin-left: auto;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    z-index: 100;
    overflow: hidden;
  }

  &__dropdown-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border: none;
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    cursor: pointer;
    transition: background 0.15s ease;
    text-align: left;

    &:hover {
      background: #F8FAFC;
    }

    &--active {
      background: #EEF4FF;
      color: #4D84FF;
    }
  }

  &__dropdown-balance {
    margin-left: auto;
    font-size: 13px;
    color: #64748B;
  }

  &__balance {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    isolation: isolate;
    overflow: hidden;

    margin-bottom: 32px;
    padding: 24px;
    // Flat brand navy. A gradient here was decoration standing in for a colour decision;
    // one deliberate surface colour reads calmer and matches the Treasury balance panel.
    background: #001240;
    border-radius: 12px;
  }

  &__balance-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__balance-label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 8px;
  }

  &__balance-amount {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 32px;
    font-weight: 500;
    color: white;

    @media screen and (min-width: 768px) {
      font-size: 40px;
    }
  }

  &__actions {
    display: grid;
    /* Fund is the primary action and gets the room; the other two are equal-weight
       alternatives. Three identical blocks gave no hint which to reach for. */
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: 12px;

    @media screen and (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  &__btn {
    transition: transform 0.12s ease, background 0.15s ease, box-shadow 0.15s ease;

    &:active {
      transform: translateY(1px);
    }

    &:focus-visible {
      outline: 2px solid #4D84FF;
      outline-offset: 2px;
    }
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 20px;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 150ms ease, transform 120ms ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--primary {
      background: #4D84FF;
      border: none;
      color: white;

      &:hover:not(:disabled) {
        background: #3B6FE8;
        transform: translateY(-1px);
      }
    }

    &--secondary {
      background: #0F172A;
      border: none;
      color: white;

      &:hover:not(:disabled) {
        background: #1E293B;
        transform: translateY(-1px);
      }
    }

    &--outline {
      background: white;
      border: 1px solid #E2E8F0;
      color: #0F172A;

      &:hover:not(:disabled) {
        border-color: #4D84FF;
        color: #4D84FF;
        background: #EEF4FF;
      }
    }
  }
}





/* A link rather than a label: the money is somewhere, and this says where. */
.wallet-card__held {
  display: block;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.78);
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
  padding-bottom: 1px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.7);
  }
}
</style>
